<div class="customers-page">
  @if (isLoading) {
    <app-loader></app-loader>
  }

  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Customer CRM</span>

      <h1>Customer <span>Relationships</span></h1>

      <p>
        Monitor customer health, loyalty, visit patterns, preferences, and
        relationship value from one CRM workspace.
      </p>

      <div class="salon-scope-pill">
        <span>Salon view</span>
        <strong>{{ salonContext.selectionLabel() }}</strong>
      </div>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--dark"
        (click)="openCustomerAdd()"
      >
        Add Customer
      </button>
    </div>
  </section>

  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Total Customers</span>
        <strong>{{
          customersStats.total_customers || customers.length || 0
        }}</strong>
        <p>{{ activeCustomerPercent }}% repeat customer rate.</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Active Customers</span>
        <strong>{{ customersStats.repeat_customers || 0 }}</strong>
        <p>Customers with repeat visit history.</p>
      </div>

      <img [src]="assetUrl + 'icons/smile-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Lifetime Value</span>
        <strong>{{ totalLifetimeValue | currency }}</strong>
        <p>Total customer value across the selected salon view.</p>
      </div>

      <img [src]="assetUrl + 'icons/dollar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>At-Risk</span>
        <strong>{{ atRiskCustomers }}</strong>
        <p>Customers needing follow-up attention.</p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>
  </section>

  <section class="customer-grid">
    <article class="panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">CRM Directory</span>
          <h2>Customer List</h2>
          <p>
            Select a customer from the current salon view to see relationship
            details.
          </p>
        </div>

        <div class="panel-actions">
          <glo-control
            icon="search"
            placeholder="Search customers"
            classes="bg-gray-50"
            (valueChange)="onSearch($event)"
          >
          </glo-control>

          <select
            class="crm-select"
            [(ngModel)]="selectedSegment"
            (change)="applyFilters()"
          >
            <option value="all">All customers</option>
            <option value="active">Active</option>
            <option value="repeat">Repeat</option>
            <option value="highValue">High value</option>
            <option value="atRisk">At risk</option>
            <option value="birthdayMissing">Missing birthday</option>
          </select>
        </div>
      </header>

      @if (filteredCustomers.length > 0) {
        <div class="table-shell">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Last Visit</th>
                <th>Visits</th>
                <th>Lifetime Value</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              @for (customer of viewCustomers; track customer.customer_id) {
                <tr
                  [class.is-selected]="
                    selectedCustomer?.customer_id === customer.customer_id
                  "
                  (click)="selectCustomer(customer)"
                >
                  <td>
                    <div class="customer-cell">
                      @if (customer.avatar || customer.image) {
                        <img
                          [src]="customer.avatar || customer.image"
                          alt=""
                          class="customer-avatar-img"
                        />
                      } @else {
                        <div class="customer-avatar">
                          {{ getInitials(customer) }}
                        </div>
                      }

                      <div>
                        <strong>
                          {{
                            customer.full_name ||
                              getFullName(customer) ||
                              "Unnamed Customer"
                          }}
                        </strong>

                        <span>
                          {{
                            customer.member_Level ||
                              customer.member_level ||
                              "Bronze"
                          }}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div class="contact-cell">
                      <strong>{{ customer.email || "No email" }}</strong>
                      <span>{{ customer.phone_number || "No phone" }}</span>
                    </div>
                  </td>

                  <td>
                    {{
                      formatDateString(customer.last_visit, "MMM D, YYYY") ||
                        "No visits"
                    }}
                  </td>

                  <td>
                    <span class="visits-pill">
                      {{ customer.total_visits || 0 }} visits
                    </span>
                  </td>

                  <td>
                    <strong class="value-text">
                      {{ toMoney(customer.lifetime_value) }}
                    </strong>
                  </td>

                  <td>
                    <span
                      class="status-pill"
                      [class.status-pill--risk]="isAtRisk(customer)"
                      [class.status-pill--healthy]="!isAtRisk(customer)"
                    >
                      {{
                        isAtRisk(customer)
                          ? "Needs attention"
                          : getFrequencyLabel(customer)
                      }}
                    </span>
                  </td>

                  <td>
                    <div class="row-actions">
                      <button
                        type="button"
                        (click)="
                          openCustomerDetails(customer);
                          $event.stopPropagation()
                        "
                      >
                        View
                      </button>

                      <button
                        type="button"
                        (click)="
                          openCustomerEdit(customer); $event.stopPropagation()
                        "
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <glo-pagination
          [totalItems]="filteredCustomers.length"
          [currentPage]="currentPage"
          [itemsPerPage]="itemsPerPage"
          [itemsPerPageOptions]="[10, 20, 50, 100]"
          (currentPageChange)="onPageChange($event)"
          (itemsPerPageChange)="onItemsPerPageChange($event)"
        >
        </glo-pagination>
      } @else {
        <div class="empty-state">
          <div class="empty-icon">C</div>

          <strong>No customers found</strong>

          <p>
            Try adjusting the search or filter, or add a new customer profile.
          </p>

          <button
            type="button"
            class="overview-btn overview-btn--dark"
            (click)="openCustomerAdd()"
          >
            Add Customer
          </button>
        </div>
      }
    </article>

    @if (selectedCustomer) {
      <aside class="panel relationship-panel">
        <div class="profile-head">
          @if (selectedCustomer.avatar || selectedCustomer.image) {
            <img
              [src]="selectedCustomer.avatar || selectedCustomer.image"
              alt=""
              class="profile-avatar-img"
            />
          } @else {
            <div class="profile-avatar">
              {{ getInitials(selectedCustomer) }}
            </div>
          }

          <div>
            <span class="panel-kicker">Relationship Profile</span>

            <h3>
              {{
                selectedCustomer.full_name ||
                  getFullName(selectedCustomer) ||
                  "Unnamed Customer"
              }}
            </h3>

            <p>{{ selectedCustomer.email || "No email on file" }}</p>
          </div>
        </div>

        <div class="profile-metrics">
          <div>
            <span>Phone</span>
            <strong>{{ selectedCustomer.phone_number || "Not added" }}</strong>
          </div>

          <div>
            <span>Birthday</span>
            <strong>
              {{
                formatDateString(selectedCustomer.birthday, "MMM D") ||
                  "Not added"
              }}
            </strong>
          </div>

          <div>
            <span>Visits</span>
            <strong>{{ selectedCustomer.total_visits || 0 }}</strong>
          </div>

          <div>
            <span>Value</span>
            <strong>{{ toMoney(selectedCustomer.lifetime_value) }}</strong>
          </div>
        </div>

        <div class="profile-section">
          <h4>Relationship Notes</h4>
          <p>
            {{
              selectedCustomer.notes ||
                selectedCustomer.internal_notes ||
                "No notes have been added yet."
            }}
          </p>
        </div>

        <div class="profile-section">
          <h4>Service Preferences</h4>

          <div class="chip-list">
            @for (
              service of getPreferredServices(selectedCustomer);
              track service
            ) {
              <span>{{ service }}</span>
            }
          </div>
        </div>

        <div class="profile-section">
          <h4>Suggested Follow-Up</h4>
          <p>{{ getFollowUpSuggestion(selectedCustomer) }}</p>
        </div>

        <div class="profile-actions">
          <button
            type="button"
            class="overview-btn overview-btn--light"
            (click)="openCustomerEdit(selectedCustomer)"
          >
            Edit
          </button>

          <button
            type="button"
            class="overview-btn overview-btn--dark"
            (click)="openCustomerDetails(selectedCustomer)"
          >
            Details
          </button>
        </div>
      </aside>
    }
  </section>

  <div class="mobile-action">
    <button
      type="button"
      class="overview-btn overview-btn--dark"
      (click)="openCustomerAdd()"
    >
      Add Customer
    </button>
  </div>
</div>
